import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useQuery } from "@tanstack/react-query";

import { getMyClubCoachProfile } from "@/libs/staffApi";

/**
 * The caller's club coach profile in an organization, or null when they have
 * none there (the server answers 404). Shared by the coaching workspace and
 * the navigation, which shows My coaching only to people who coach.
 *
 * "No profile" is a resolved answer, not a failure: a 404 is never retried and
 * a newly mounted reader never refetches a settled answer, so the route gate
 * and the page cannot re-trigger each other.
 */
export function useMyClubCoachProfile(orgId: string | null | undefined) {
  const { user } = useAuth();
  const actorId = user?.uid ?? "";
  return useQuery({
    queryKey: ["coaching-profile", actorId, orgId],
    queryFn: async () => {
      try {
        return await getMyClubCoachProfile(orgId!);
      } catch (error) {
        if (isApiError(error) && error.status === 404) return null;
        throw error;
      }
    },
    enabled: Boolean(actorId && orgId),
    // Changes only when the club publishes or archives the profile.
    staleTime: 5 * 60_000,
    retry: false,
    retryOnMount: false,
  });
}
